import { useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { captureLead } from '../../services/leadCapture';
import { LEAD_SOURCES } from '../../services/contactApi';

interface ConsultingCtaForm {
  email: string;
}

const POPIA_NOTICE =
  'POPIA notice: your email is used for consulting follow-up only. We will not share it with third parties.';

export default function ConsultingCta() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ConsultingCtaForm>();

  const onSubmit: SubmitHandler<ConsultingCtaForm> = async ({ email }) => {
    setIsSubmitting(true);
    setStatus(null);

    try {
      const result = await captureLead({ email, source: LEAD_SOURCES.CONSULTING });
      setStatus({
        type: 'success',
        message: result.message || "Thanks! We'll be in touch about your consulting enquiry.",
      });
      reset();
    } catch {
      setStatus({
        type: 'error',
        message: 'Something went wrong. Please try again later.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      aria-labelledby="consulting-cta-title"
      className="mt-8 bg-gradient-to-r from-sa-green to-emerald-600 rounded-xl p-6 sm:p-8 text-white shadow-lg"
    >
      <h3 id="consulting-cta-title" className="text-lg font-bold mb-1">
        Want to explore what this means for your business? Let&rsquo;s chat.
      </h3>
      <p className="text-white/90 text-sm mb-4">
        Drop your email and a consultant will reach out to talk through what these numbers mean for
        your payroll or business.
      </p>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-2">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <label htmlFor="consulting-email" className="sr-only">
              Email address
            </label>
            <input
              id="consulting-email"
              type="email"
              autoComplete="email"
              inputMode="email"
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Please enter a valid email address',
                },
              })}
              className="h-12 w-full px-5 rounded-lg text-charcoal focus:ring-2 focus:ring-white focus:ring-offset-0 focus:outline-none disabled:opacity-60"
              placeholder="you@example.com"
              disabled={isSubmitting}
              aria-invalid={!!errors.email}
              aria-describedby="consulting-popia-notice"
            />
          </div>

          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex h-12 items-center justify-center rounded-lg bg-white px-6 font-medium text-sa-green whitespace-nowrap transition-colors hover:bg-green-50 disabled:opacity-60"
          >
            {isSubmitting ? 'Sending…' : "Let's chat"}
          </button>
        </div>

        {errors.email && (
          <p className="text-sm text-amber-100" role="alert">
            {errors.email.message}
          </p>
        )}
        <p id="consulting-popia-notice" className="text-xs text-white/90">
          {POPIA_NOTICE}
        </p>
      </form>

      {status && (
        <p
          role="status"
          aria-live="polite"
          className={`mt-3 text-sm ${status.type === 'success' ? 'text-white/90' : 'text-amber-100'}`}
        >
          {status.message}
        </p>
      )}
    </section>
  );
}
